// React
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
// React Query
// Redux
// MUI
import { Stack, Typography } from "@mui/material";

// API
// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import useOnTransferChangeEventSubscription from "./hooks/useOnTransferChangeEventSubscription";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import useGPSDeviceTransferringSubscription from "@DataPages/components/Inventory/hooks/useGPSDeviceTransferringSubscription";

// Contexts
import { InventoryFilterItem, useInventoryContext } from "./contexts/InventoryFilterContext";

// Components
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import TabSelectable from "@Layouts/components/Tab/TabSelectable";
import AssetLocationManagement from "@DataPages/components/Inventory/AssetLocationManagement";
import ConsumerRegistrationStatusManagement from "@DataPages/components/Inventory/ConsumerRegistrationStatusManagement";
import KeyManagementSystem from "@DataPages/components/Inventory/KeyActivityManagement";
import VehicleInformation from "@DataPages/components/Inventory/VehicleInformation";
import Reconciliation from "@DataPages/components/Inventory/TabsSections/Reconciliation";
import DealerTrades from "./DealerTrades";
import ClearSelectionButton from "@DataPages/components/Inventory/Layout/ClearSelectionButton";
import TransferRequest from "./Widgets/DealerTrades/TransferRequest";

// Services
import actions from "@Constants/actionsList";

// Icons
import { ReactComponent as InfoIcon } from "@/assets/icons/dataPages/inventory/information.svg";

// Constants
import { FIXED_SIZE_HEIGHT_TABS, InventoryTab } from "./constants";

// Types
import { VehicleCategory } from "@DataPages/types/inventory/commons";
import { ILocation } from "@Common/interfaces/location";
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";
import { getLogger } from "@/Common/helpers/getLogger";
import { TActionToDo } from "./types/dealerTrades";
import { IAdministrationTools, IGlobalPositioningService } from "@Settings/types/administrationTools/model";
import useConfigurationData from "@Common/hooks/Configuration/useConfigurationData.ts";
import moment from "moment";

// Helpers
import { getDateByTimeZone } from "@dealerwave/dw-layer-date-converter";
import ScrollButton from "@/Layouts/components/Buttons/ScrollButton";

type InventoryBodyProps = {
	selected: IPayPeriod | null;
};

const log = getLogger("InventoryBody");

/*********************************************** HELPERS ***********************************************/
function getVehicleCategory(tab: number): VehicleCategory {
	switch (tab) {
		case 0:
			return "new";
		case 1:
			return "preowned";
		default:
			return "new";
	}
}

function hasIntegration(location: ILocation, type: string) {
	return location?.services?.some((service) => service.type === type) ?? false;
}

type TGpsProvider = Pick<IGlobalPositioningService, "lojack">;
const availableGPSIntegrations: (keyof TGpsProvider)[] = ["lojack"];
function hasAssetLocationManagementPermission(
	location: ILocation,
	hasGPSPermission: boolean,
	administrationTools: IAdministrationTools
): boolean {
	if (!location) {
		return false;
	}

	const hasGPSIntegration = hasIntegration(location, "GPS");

	const gpsProviderKeys = Object.keys(
		administrationTools?.integrations?.globalPositioningService ?? {}
	) as (keyof IGlobalPositioningService)[];

	const filteredGPSProviderKeys = gpsProviderKeys.filter((provider) =>
		availableGPSIntegrations.includes(provider as keyof TGpsProvider)
	) as unknown as (keyof TGpsProvider)[];

	const hasGPSProvider = Boolean(filteredGPSProviderKeys.length);
	return hasGPSIntegration && hasGPSPermission && hasGPSProvider;
}

// Constants
const assetLocationManagementTabNumber = 2;
const keyActivityManagementTabNumber = 3;
const dealerTradesTabNumber = 4;
const reconciliationTabNumber = 5;

const resolveBadgeValue = (value: number) => {
	return value ? value.toString() : "";
};

const isSameFilterOrigin = (item: InventoryFilterItem, origin: InventoryTab, commissionCycleId: string, payPeriodId: string) => {
	return item.origin === origin && item.commissionCycleId === commissionCycleId && item.payPeriodId === payPeriodId;
};

export default function InventoryBody(props: Readonly<InventoryBodyProps>) {
	log("Rendering InventoryBody");
	const { selected } = props;
	const { useAuth } = useAuthorization();
	const location = useLocation();
	const locationId = location?.locationId;
	const accountId = location?.services?.find((service) => service.type === "GPS")?.accountId;
	const periodId = selected?.payPeriodId ?? "";
	const updateNowBtnDisabled = !selected?.status || selected?.status === "COMPLETED";
	const commissionCycleId = selected?.cycleId ?? "";
	const { setPayPeriodId, setCommissionCycleId } = useInventoryContext();
	const dealerTradesActionRef = useRef<TActionToDo>("");

	const { clearCache } = useCacheCleaner();

	const { clearFilterSelection, filterItems } = useInventoryContext();

	const [tabSelected, setTabSelected] = useState(0);

	const vehicleCategory = getVehicleCategory(Number(tabSelected));

	const hasGPSPermission = [actions.VIEW_GPS_EMBEDDED_URL_BASIC_USER, actions.VIEW_GPS_EMBEDDED_URL_ADMIN_USER].some(useAuth);

	const hasPermissionToEdit = useAuth(actions.EDIT_DEALER_TRADES);
	const hasReconciliationPermission = useAuth(actions.VIEW_RECONCILIATION);
	const hasDealerTradesPermission = useAuth(actions.VIEW_DEALER_TRADES);

	const { configuration } = useConfigurationData({ locationId });
	const { administrationTools } = configuration ?? {};

	/********************************************** HELPERS ***********************************************/
	const isTabDisabled = (tab: number) => {
		switch (tab) {
			case 0: // 0 New Vehicles
			case 1: // 1 Pre-owned Vehicles
				return false;
			case assetLocationManagementTabNumber:
				return !hasAssetLocationManagementPermission(location, hasGPSPermission, administrationTools as IAdministrationTools);
			case keyActivityManagementTabNumber:
				return !hasIntegration(location, "KMS");
			case dealerTradesTabNumber:
				return !hasDealerTradesPermission;
			case reconciliationTabNumber:
				return !hasReconciliationPermission;
			default:
				return false;
		}
	};

	/********************************************** HANDLERS **********************************************/
	const handleShowMultipleGPSVehicleDetails = useCallback(() => {
		setTabSelected(assetLocationManagementTabNumber);
	}, []);

	useOnTransferChangeEventSubscription({ locationId });
	useGPSDeviceTransferringSubscription({ locationId, vehicleCategory, selectedPPId: periodId, selectedCCId: commissionCycleId });

	const isCurrentPayPeriod = useMemo(() => {
		if (selected) {
			const now = getDateByTimeZone(location.timezone);
			return moment(now).isBetween(moment(selected.startDate), moment(selected.endDate), null, "[]");
		}
		return false;
	}, [selected]);

	const memoizedContentMap = useMemo(() => {
		return [
			<VehicleInformation
				locationId={locationId ?? ""}
				accountId={accountId ?? ""}
				key={1}
				vehicleCategory={vehicleCategory}
				onShowMultipleGPSVehicleDetails={handleShowMultipleGPSVehicleDetails}
				selectedPayPeriod={selected as IPayPeriod}
			/>,
			<VehicleInformation
				locationId={locationId ?? ""}
				accountId={accountId ?? ""}
				key={2}
				vehicleCategory={vehicleCategory}
				onShowMultipleGPSVehicleDetails={handleShowMultipleGPSVehicleDetails}
				selectedPayPeriod={selected as IPayPeriod}
			/>,
			<AssetLocationManagement key={3} />,
			<KeyManagementSystem key={4} />,
			<DealerTrades key={5} locationId={locationId} actionRef={dealerTradesActionRef} isCurrentPayPeriod={isCurrentPayPeriod} />,
			<Reconciliation key={6} selectedPayPeriod={selected as IPayPeriod} locationId={locationId} />,
		];
	}, [vehicleCategory, locationId, selected, dealerTradesActionRef, accountId, handleShowMultipleGPSVehicleDetails, isCurrentPayPeriod]);

	if (isTabDisabled(tabSelected)) {
		setTabSelected(0);
	}

	const badgeValues = filterItems.reduce(
		(acc, item) => ({
			new: acc.new + (isSameFilterOrigin(item, InventoryTab.NEW_VEHICLES, commissionCycleId, periodId) ? 1 : 0),
			preOwned: acc.preOwned + (isSameFilterOrigin(item, InventoryTab.PREOWNED_VEHICLES, commissionCycleId, periodId) ? 1 : 0),
			kms: acc.kms + (isSameFilterOrigin(item, InventoryTab.KMS, commissionCycleId, periodId) ? 1 : 0),
			trades: acc.trades + (isSameFilterOrigin(item, InventoryTab.DEALER_TRADES, commissionCycleId, periodId) ? 1 : 0),
			reconciliation:
				acc.reconciliation + (isSameFilterOrigin(item, InventoryTab.RECONCILIATION, commissionCycleId, periodId) ? 1 : 0),
		}),
		{ new: 0, preOwned: 0, kms: 0, trades: 0, reconciliation: 0 }
	);

	useEffect(() => {
		if (selected) {
			log("Setting pay period and commission cycle id on tree selection change");
			setPayPeriodId(selected.payPeriodId);
			setCommissionCycleId(selected.cycleId);
		}
		return () => {
			clearCache(["inventory"]);
		};
	}, [selected]);

	return (
		<>
			<RoundedBox className={clsx({ "min-h-[700px] !px-0 mb-4": true, "!h-[740px]": FIXED_SIZE_HEIGHT_TABS.includes(tabSelected) })}>
				<Stack justifyContent={"space-between"} minHeight={740} position={"relative"} spacing={2} pt={2}>
					<TabSelectable
						tabsProps={{
							tabClasses: "!min-w-[900px]",
						}}
						tabs={[
							{
								title: "New Vehicles",
								disabled: isTabDisabled(0),
								badgeValue: resolveBadgeValue(badgeValues.new),
							},
							{
								title: "Pre-owned Vehicles",
								disabled: isTabDisabled(1),
								badgeValue: resolveBadgeValue(badgeValues.preOwned),
							},
							{
								title: "Asset Location Management",
								disabled: isTabDisabled(2),
							},
							{
								title: "Key Activity Management",
								disabled: isTabDisabled(3),
								badgeValue: resolveBadgeValue(badgeValues.kms),
							},
							{
								title: "Dealer Trades",
								disabled: isTabDisabled(4),
								badgeValue: resolveBadgeValue(badgeValues.trades),
							},
							{
								title: "Reconciliation",
								disabled: isTabDisabled(5),
								badgeValue: resolveBadgeValue(badgeValues.reconciliation),
							},
						]}
						tabSelected={tabSelected}
						content={memoizedContentMap}
						onTabChange={setTabSelected}
						useMUITabPanel
						customComponent={
							<div className={tabSelected !== 4 ? "hidden" : ""}>
								<TransferRequest
									locationId={locationId ?? ""}
									commissionCycleId={commissionCycleId}
									payPeriodId={periodId ?? ""}
									editPermission={hasPermissionToEdit}
									isCurrentPayPeriod={isCurrentPayPeriod}
								/>
							</div>
						}
					/>

					{tabSelected === 0 || tabSelected === 1 ? (
						<Stack direction={"row"} className={"!absolute bottom-10 left-7"} alignItems={"center"} spacing={1}>
							<InfoIcon />
							<Typography className={"!font-sans !text-[14.5px] !text-[#666666] "}>
								Key status is effective as of: Last Night
							</Typography>
						</Stack>
					) : null}

					{tabSelected === assetLocationManagementTabNumber ? (
						<div className="fixed bottom-12 left-[calc(100%-210px)] z-20">
							<ScrollButton />
						</div>
					) : null}
				</Stack>
				<ClearSelectionButton onClearSelection={clearFilterSelection} hasFilteredItems={!!filterItems.length} />
			</RoundedBox>

			{tabSelected === 2 ? (
				<RoundedBox className="!h-fit !pb-[48px]">
					<Stack minHeight={740} position={"relative"}>
						<ConsumerRegistrationStatusManagement
							locationId={locationId ?? ""}
							commissionCycleId={commissionCycleId}
							payPeriodId={periodId ?? ""}
							updateNowBtnDisabled={updateNowBtnDisabled}
							tableTitle={selected?.titleExtended.replace("Semi-monthly", "") ?? ""}
						/>
					</Stack>
				</RoundedBox>
			) : null}
		</>
	);
}
